import { APP_ENVS, COMPANY_BASE_URL } from '@/shared/constants/envs';

import { MCP_FAQ_DATA, MCP_UPDATED } from '../../constants/mcp-content';

const PAGE_URL = `${COMPANY_BASE_URL}/mcp`;

const webPageSchema = {
	'@context': 'https://schema.org',
	'@type': 'WebPage',
	'@id': `${PAGE_URL}/#webpage`,
	url: PAGE_URL,
	name: `ATS MCP Server — Use ${APP_ENVS.COMPANY_NAME} in Claude, ChatGPT & Cursor | ${APP_ENVS.APP_TITLE}`,
	description: `The ATS MCP server: connect ${APP_ENVS.COMPANY_NAME} to Claude, ChatGPT, Codex or Cursor to screen candidates, import data, create jobs and schedule interviews. Read and write, included on the free plan.`,
	isPartOf: {
		'@type': 'WebSite',
		'@id': `${COMPANY_BASE_URL}/#website`,
	},
	about: {
		'@type': 'SoftwareApplication',
		'@id': `${PAGE_URL}/#software`,
	},
	keywords: [
		'ATS MCP server',
		'ATS with MCP',
		'MCP for recruiting',
		'Model Context Protocol ATS',
		'Claude ATS integration',
		'ChatGPT ATS integration',
		'AI recruiting agent',
	].join(', '),
	dateModified: MCP_UPDATED,
	inLanguage: 'en-US',
};

const softwareApplicationSchema = {
	'@context': 'https://schema.org',
	'@type': 'SoftwareApplication',
	'@id': `${PAGE_URL}/#software`,
	name: `${APP_ENVS.COMPANY_NAME} MCP server`,
	applicationCategory: 'BusinessApplication',
	operatingSystem: 'Web',
	url: PAGE_URL,
	description: `${APP_ENVS.COMPANY_NAME}'s MCP server connects your hiring workspace to Claude, ChatGPT, Codex, Grok, Cursor and any other Model Context Protocol client, so an agent can screen candidates in batches, run analytics queries, search the candidate database, import data, create jobs and schedule, reschedule or cancel interviews on your behalf. Included on every plan, including Free.`,
	offers: {
		'@type': 'Offer',
		price: '0',
		priceCurrency: 'USD',
		description: 'MCP server included on the free plan with no request cap; paid plans have higher rate limits.',
	},
	featureList: [
		'Batch candidate fetch for screening a whole pipeline',
		'Screening results written back as a filterable column',
		'Full analytics query language exposed to the agent, with drill downs',
		'Candidate search across the workspace, including resume content',
		'Stage changes, comments, labels and interview feedback',
		'Interview management: check interviewer open slots, schedule, send booking links, reschedule and cancel',
		'Candidate import from sheets, forms and email',
		'Job creation, configuration and publishing',
		'Per-user access that inherits existing workspace permissions',
	],
};

const breadcrumbSchema = {
	'@context': 'https://schema.org',
	'@type': 'BreadcrumbList',
	'@id': `${PAGE_URL}/#breadcrumb`,
	itemListElement: [
		{
			'@type': 'ListItem',
			position: 1,
			name: 'Home',
			item: `${COMPANY_BASE_URL}/`,
		},
		{
			'@type': 'ListItem',
			position: 2,
			name: 'MCP',
			item: PAGE_URL,
		},
	],
};

const faqPageSchema = {
	'@context': 'https://schema.org',
	'@type': 'FAQPage',
	'@id': `${PAGE_URL}/#faq`,
	url: PAGE_URL,
	mainEntity: MCP_FAQ_DATA.map((faq) => ({
		'@type': 'Question',
		name: faq.question,
		acceptedAnswer: {
			'@type': 'Answer',
			text: faq.answer,
		},
	})),
};

/**
 * Structured data for the /mcp SEO landing page.
 * - WebPage schema: page-level identity and keyword signals
 * - SoftwareApplication schema: signals this is a software feature with a real feature list
 * - BreadcrumbList schema: reinforces site structure, enables breadcrumb rich results
 * - FAQPage schema: enables FAQ rich results in SERPs, targeting PAA queries
 */
export function McpStructuredData() {
	return (
		<>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationSchema) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageSchema) }}
			/>
		</>
	);
}
